import { User } from 'firebase/auth';

interface UserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface TokenExpiryInfo {
  expiresAt: number;
  isExpired: boolean;
  timeUntilExpiry: number;
}

class AuthService {
  private readonly TOKEN_KEY = 'authToken';
  private readonly USER_DATA_KEY = 'userData';

  /**
   * Store authentication token in localStorage
   */
  storeToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Retrieve authentication token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Store user data in localStorage
   */
  storeUserData(user: User): void {
    const userData: UserData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
    };
    localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
  }

  /**
   * Retrieve user data from localStorage
   */
  getUserData(): UserData | null {
    const userDataStr = localStorage.getItem(this.USER_DATA_KEY);
    if (!userDataStr) return null;
    
    try {
      return JSON.parse(userDataStr);
    } catch (error) {
      console.error('Failed to parse user data:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const userData = this.getUserData();
    return !!(token && userData);
  }

  /**
   * Clear all authentication data
   */
  clearAllAuthState(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
  }

  /**
   * Get token expiry information
   */
  getTokenExpiryInfo(): TokenExpiryInfo | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      // Decode JWT token to get expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const isExpired = timeUntilExpiry <= 0;

      return {
        expiresAt,
        isExpired,
        timeUntilExpiry: Math.max(0, timeUntilExpiry),
      };
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const expiryInfo = this.getTokenExpiryInfo();
    return expiryInfo ? expiryInfo.isExpired : true;
  }

  /**
   * Get time until token expires (in milliseconds)
   */
  getTimeUntilExpiry(): number {
    const expiryInfo = this.getTokenExpiryInfo();
    return expiryInfo ? expiryInfo.timeUntilExpiry : 0;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
